import { STATES } from "./states";

export interface PayoutRow {
  state: string;
  abbreviation: string;
  slug: string;
  stateTaxRate: number; // percent
  lumpSumNet: number;
  annualAnnuityNet: number;
  totalAnnuityNet: number;
  notes?: string;
}

const STATE_TAX: Record<string, number> = {
  AL: 5, AK: 0, AZ: 4.5, AR: 5.9, CA: 0, CO: 4.4, CT: 6.99, DE: 0, DC: 8.95,
  FL: 0, GA: 5.49, HI: 0, ID: 5.8, IL: 4.95, IN: 3.15, IA: 6, KS: 5, KY: 4.5,
  LA: 4.25, ME: 7.15, MD: 8.95, MA: 5, MI: 4.25, MN: 9.85, MS: 5, MO: 4, MT: 6.75,
  NE: 5, NV: 0, NH: 0, NJ: 8, NM: 5.9, NY: 10.9, NC: 4.75, ND: 2.9, OH: 3.99,
  OK: 4.75, OR: 9.9, PA: 3.07, RI: 5.99, SC: 6.4, SD: 0, TN: 0, TX: 0, UT: 4.85,
  VT: 8.75, VA: 5.75, WA: 0, WV: 6.5, WI: 7.65, WY: 0,
};

const FEDERAL_RATE = 0.37; // top federal bracket applies to jackpots
const FEDERAL_WITHHOLDING = 0.24; // amount withheld up-front

export interface JackpotAnalysisInput {
  advertisedJackpot: number; // e.g. 100_000_000
  cashRatio: number; // 0.49 Powerball, 0.50 Mega Millions
  annuityYears?: number; // default 30
}

export function buildPayoutRows({
  advertisedJackpot,
  cashRatio,
  annuityYears = 30,
}: JackpotAnalysisInput): PayoutRow[] {
  const cashValue = advertisedJackpot * cashRatio;
  const annualGross = advertisedJackpot / annuityYears;

  return STATES.filter((s) => s.has_lottery).map((s) => {
    const rate = (STATE_TAX[s.abbreviation] ?? 0) / 100;
    const totalRate = FEDERAL_RATE + rate;
    const lumpSumNet = cashValue * (1 - totalRate);
    const annualAnnuityNet = annualGross * (1 - totalRate);
    return {
      state: s.name,
      abbreviation: s.abbreviation,
      slug: s.slug,
      stateTaxRate: STATE_TAX[s.abbreviation] ?? 0,
      lumpSumNet,
      annualAnnuityNet,
      totalAnnuityNet: annualAnnuityNet * annuityYears,
    };
  });
}

export interface PayoutFaq {
  question: string;
  answer: string;
}

export function jackpotAnalysisFaqs(game: "Powerball" | "Mega Millions"): PayoutFaq[] {
  const cashPct = game === "Powerball" ? "≈49%" : "≈50%";
  return [
    {
      question: `How is the ${game} cash (lump-sum) value calculated?`,
      answer: `${game}'s advertised jackpot is the 30-year annuity value. The cash option is the amount of money the lottery has on hand to fund the annuity — currently ${cashPct} of the advertised amount. The exact ratio moves with U.S. Treasury bond yields, because the lottery buys government bonds to fund annuity payments.`,
    },
    {
      question: `What federal taxes apply to a ${game} jackpot?`,
      answer:
        "The IRS withholds 24% up front on any lottery prize above $5,000. A jackpot pushes the winner into the top 37% federal bracket, so the additional 13% is owed at tax filing time. Our table shows the full 37% federal rate so the net you see matches what you actually keep.",
    },
    {
      question: "Which states do not tax lottery winnings?",
      answer:
        "California, Florida, New Hampshire, Nevada, South Dakota, Tennessee, Texas, Washington, and Wyoming do not tax lottery prizes. Delaware does not tax winnings for residents but withholds for non-residents. Buying a ticket in a no-tax state when you live in a high-tax state does not avoid the tax — your state of residence taxes the prize.",
    },
    {
      question: `What are the odds of winning ${game}?`,
      answer:
        game === "Powerball"
          ? "The odds of winning the Powerball jackpot are 1 in 292,201,338. Overall odds of winning any Powerball prize are 1 in 24.87."
          : "The odds of winning the Mega Millions jackpot are 1 in 290,472,336 (effective April 2025 rule change). Overall odds of winning any prize are 1 in 23.",
    },
    {
      question: "Lump sum vs. annuity — which is better?",
      answer:
        "The lump sum gives you immediate control of a smaller after-tax amount you can invest. The annuity pays 30 graduated installments (each 5% larger than the last) and locks in a lower effective tax rate per year for many winners. Most jackpot winners take the cash option, but financial advisors note the annuity provides protection against poor investment decisions and overspending.",
    },
    {
      question: "Are these payout numbers exact?",
      answer:
        "They are accurate estimates based on the current advertised jackpot, the published cash ratio, the 37% top federal bracket, and each state's top marginal income tax rate. Actual take-home depends on additional withholding, local/municipal taxes (e.g. New York City adds ~3.876%), residency rules, and any deductions. Consult a tax professional before claiming.",
    },
  ];
}

export const PAYOUT_SOURCES = [
  { label: "Powerball — Prizes & Odds", url: "https://www.powerball.com/games/home" },
  { label: "Mega Millions — How to Play", url: "https://www.megamillions.com/how-to-play" },
  { label: "IRS Publication 525 — Lottery Winnings", url: "https://www.irs.gov/forms-pubs/about-publication-525" },
  { label: "Tax Foundation — State Individual Income Tax Rates", url: "https://taxfoundation.org/data/all/state/state-income-tax-rates/" },
];